import React, {Component} from 'react';
import {WarningIcon} from '../../../../../images/WarningIcon';
import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../../containers/event_manager';
import ToastProgress from './toast_progress';

export const createToast = message => dispatch(EVENT_ID.SHOW_TOAST, message);

export class Toaster extends Component {
	constructor(props) {
		super(props);
		this.state = {
			toastQueue: [],
			isDisplayingToast: false,
			toastState: 0,
			toastMessage: '',
			toastActionText: '',
			toastActionCallback: null,
			toastWarning: null,
			isDisplayingDuration: null,
			duration: null,
			callback: null,
		};
		// Not state: toggling it must never trigger a render, only gate dismissToast.
		this.hidePending = false;

		this.addToastToQueue = this.addToastToQueue.bind(this);
		this.dismissToast = this.dismissToast.bind(this);
	}

	componentDidMount() {
		subscribe(EVENT_ID.SHOW_TOAST, this.addToastToQueue);
		subscribe(EVENT_ID.HIDE_TOAST, this.dismissToast);
	}

	componentWillUnmount() {
		unsubscribe(EVENT_ID.SHOW_TOAST, this.addToastToQueue);
		unsubscribe(EVENT_ID.HIDE_TOAST, this.dismissToast);
		if (this.hideToastTimeout) {
			clearTimeout(this.hideToastTimeout);
		}
	}

	/**
	 * Hides whatever toast is on display right now, and lets the queue move on.
	 *
	 * A toast created with `duration: null` never sets an auto-hide timer, so this is the only
	 * thing that can take it off the screen. Safe to call with nothing displayed, and safe to
	 * call for a timed toast: the pending timer is cleared first so it cannot fire against the
	 * toast that follows.
	 *
	 * Guarded on `hidePending` rather than `toastState`, which only reaches 1 on a 100ms timer
	 * after dispatch: a `toastState`-based guard is inert for a toast's first 100ms, so a second
	 * toast queued in that window would call this again mid-fade and shift the wrong toast out
	 * (see `hideToast`/`moveToastQueue`).
	 */
	dismissToast() {
		if (!this.state.isDisplayingToast || this.hidePending) return;
		if (this.hideToastTimeout) {
			clearTimeout(this.hideToastTimeout);
		}
		this.hideToast();
	}

	addToastToQueue(toast) {
		const {toastQueue} = this.state;
		toastQueue.push(toast);
		this.setState({toastQueue});
		if (toastQueue.length === 1) {
			this.dispatchToast();
		} else if (this.state.isDisplayingToast) {
			// Cut the current toast short so the new one is not stuck behind it. Keyed off what is
			// actually on screen rather than off the pending timer, because a `duration: null` toast
			// has no timer at all and would otherwise hold the queue forever.
			this.dismissToast();
		}
	}

	dispatchToast() {
		const toast = this.state.toastQueue[0];
		this.setState({
			isDisplayingToast: true,
			toastMessage: toast.message,
			toastActionText: toast.actionText,
			toastActionCallback: toast.actionCallback,
			toastWarning: toast.warning,
			toastVariant: toast.variant,
			isDisplayingDuration: toast.isDisplayingDuration,
			duration: toast.duration,
			callback: toast.callback,
		});
		setTimeout(() => {
			this.setState({toastState: 1});
		}, 100);
		// A null duration means "stay until something dismisses it". Without this guard
		// setTimeout(fn, null) coerces to 0 and the toast would vanish on the next tick.
		// Every other caller passes a number, including 0, and is unaffected.
		if (toast.duration !== null) {
			this.hideToastTimeout = setTimeout(() => {
				this.hideToast();
				if (this.state.callback) {
					this.state.callback();
				}
			}, toast.duration);
		}
	}

	hideToast() {
		this.hideToastTimeout = null;
		this.hidePending = true;
		this.setState({toastState: 0});
		setTimeout(() => {
			this.moveToastQueue();
		}, 200);
	}

	moveToastQueue() {
		this.hidePending = false;
		const toastQueue = this.state.toastQueue;
		toastQueue.shift();
		this.setState({isDisplayingToast: false, toastQueue});
		if (toastQueue.length) {
			this.dispatchToast();
		}
	}

	handleActionClick() {
		if (this.state.toastActionCallback) {
			this.state.toastActionCallback();
			// Goes through dismissToast rather than testing for a pending timer, so that a toast
			// with no auto-hide still closes when its action is taken.
			this.dismissToast();
		}
	}

	render() {
		return (
			<div className="toaster">
				{this.state.isDisplayingToast ? (
					<div
						className={
							'toast ' +
							(this.state.toastVariant ? this.state.toastVariant + ' ' : '') +
							(this.state.toastState === 1 ? 'displaying' : '')
						}
						data-cy="toast"
						role="status"
						aria-live="polite"
					>
						{this.state.isDisplayingDuration && <ToastProgress duration={this.state.duration} />}
						<div className="message" data-cy="toast-message">
							{this.state.toastWarning ? <WarningIcon danger={true} /> : null}
							{this.state.toastMessage}
						</div>
						{this.state.toastActionText && this.state.toastActionCallback ? (
							<div className="action" role="alert" onClick={this.handleActionClick.bind(this)}>
								{this.state.toastActionText}
							</div>
						) : null}
					</div>
				) : null}
			</div>
		);
	}
}
